import * as Icons from "@nova/component-icons"
import { ScrollableArea } from "@nova/component-scrollable-area"
import classNames from "classnames"
import { useAppDispatch, useAppSelector } from "integrations/redux/hooks"
import { selectActiveWorkspaceId } from "features/data-management/workspace/WorkspaceManagementSlice"
import {
    AdsbTrafficUpdateEvent,
    useAdsbTrafficUpdates,
} from "features/map/layers/aircraft-tracking/useAdsbTrafficUpdates"
import {
    AIRCRAFT_POPUP_DIMENSIONS,
    AIRCRAFT_POPUP_MARGIN,
} from "features/map/layers/aircraft-tracking/layers"
import { getAircraftLivePosition } from "features/map/layers/aircraft-tracking/aircraftLivePositionStore"
import { setNewViewstate } from "features/map/GlobalViewState"
import {
    selectAdsbEnabled,
    selectAdsbTrafficUpdatesAltitudeMaxFeet,
    selectAdsbTrafficUpdatesAltitudeMinFeet,
    selectAdsbTrafficUpdatesAltitudeUnit,
    selectAdsbTrafficUpdatesEnabled,
    setActivePopup,
} from "../slice"
import { useEffect, useMemo, useRef, useState } from "react"
import { ExpandableContent } from "@nova/component-expandable-content"
import { ADSB_DEAD_RECKONING_INTERVAL, DistanceUnit, formattedDistanceUnit } from "@nova/constants"
import { convertDistance } from "@nova/format"

export const AdsbTrafficUpdatesPanel = () => {
    const adsbEnabled = useAppSelector(selectAdsbEnabled)
    const trafficUpdatesEnabled = useAppSelector(selectAdsbTrafficUpdatesEnabled)
    const orgId = useAppSelector(selectActiveWorkspaceId())

    if (!adsbEnabled || !trafficUpdatesEnabled || !orgId) return null
    return <AdsbTrafficUpdatesPanelContent orgId={orgId} />
}

const AdsbTrafficUpdatesPanelContent = ({ orgId }: { orgId: string }) => {
    const [expanded, setExpanded] = useState(true)
    const { events, dismissEvent, aircraft } = useAdsbTrafficUpdates(orgId)
    const minAltitudeFeet = useAppSelector(selectAdsbTrafficUpdatesAltitudeMinFeet)
    const maxAltitudeFeet = useAppSelector(selectAdsbTrafficUpdatesAltitudeMaxFeet)
    const altitudeUnit = useAppSelector(selectAdsbTrafficUpdatesAltitudeUnit)
    const scrollRef = useRef<HTMLDivElement>(null)
    const contentRef = useRef<HTMLDivElement>(null)

    const trackedAircraftIds = useMemo(() => new Set((aircraft ?? []).map((a) => a.id)), [aircraft])

    useEffect(() => {
        const scrollable = scrollRef.current
        const content = contentRef.current
        if (!scrollable || !content) return
        const observer = new ResizeObserver(() => {
            scrollable.scrollTop = scrollable.scrollHeight
        })
        observer.observe(content)
        return () => observer.disconnect()
    }, [events])

    return (
        <div
            className="group pointer-events-auto hover:!bg-gray-800 transition-colors rounded-lg w-56 flex flex-col"
            style={{
                background:
                    "linear-gradient(180deg, rgba(12, 12, 15, 0.00) 0%, rgba(12, 12, 15, 0.20) 15%, rgba(12, 12, 15, 0.50) 100%)",
            }}
        >
            <ExpandableContent isOpen={expanded}>
                <div className="min-h-[32px]">
                    {!events.length && (
                        <div className="text-xs text-gray-400 p-2 w-full text-center">
                            No traffic updates
                        </div>
                    )}
                    {!!events.length && (
                        <div className="py-2">
                            <ScrollableArea
                                shouldPreventScrollbarShifting
                                ref={scrollRef}
                                className="max-h-28 px-2 group-[&:not(:hover)]:[--scrollable-area-thumb-color:transparent] group-[&:not(:hover)]:[--scrollable-area-track-color:transparent]"
                            >
                                <div ref={contentRef} className="flex flex-col">
                                    {events.map((event) => (
                                        <AdsbTrafficUpdateRow
                                            key={event.id}
                                            event={event}
                                            onExpire={dismissEvent}
                                            isOnMap={trackedAircraftIds.has(event.aircraft.id)}
                                        />
                                    ))}
                                </div>
                            </ScrollableArea>
                        </div>
                    )}
                </div>
            </ExpandableContent>
            <div className="flex items-center p-2">
                <div className="grow shrink min-w-0 truncate text-white-400">
                    Traffic ·{" "}
                    {Math.round(
                        convertDistance(minAltitudeFeet, DistanceUnit.Feet, altitudeUnit)
                    ).toLocaleString()}{" "}
                    -{" "}
                    {Math.round(
                        convertDistance(maxAltitudeFeet, DistanceUnit.Feet, altitudeUnit)
                    ).toLocaleString()}{" "}
                    {formattedDistanceUnit[altitudeUnit].abbrev}
                </div>
                <Icons.SmallIcon
                    Icon={expanded ? Icons.Minus : Icons.Plus}
                    className="text-gray-300 hover:text-gray-100 cursor-pointer"
                    onClick={() => setExpanded((v) => !v)}
                />
            </div>
        </div>
    )
}

const ROW_FADE_START_MS = 6000
const ROW_FADE_DURATION_MS = 2000
const ROW_OPEN_CLOSE_DURATION_MS = 200

const AdsbTrafficUpdateRow = ({
    event,
    onExpire,
    isOnMap,
}: {
    event: AdsbTrafficUpdateEvent
    onExpire: (event: AdsbTrafficUpdateEvent) => void
    isOnMap: boolean
}) => {
    const dispatch = useAppDispatch()
    const [phase, setPhase] = useState<"entering" | "visible" | "fading" | "exiting">("entering")
    const [isHovered, setIsHovered] = useState(false)
    const type = event.kind === "entered" ? "Inbound" : "Outbound"
    const isPaused = isHovered && isOnMap

    useEffect(() => {
        setPhase("visible")
    }, [])

    useEffect(() => {
        if (phase !== "visible" || isPaused) return
        const fadeStartTimer = setTimeout(() => setPhase("fading"), ROW_FADE_START_MS)
        return () => clearTimeout(fadeStartTimer)
    }, [phase, isPaused])
    useEffect(() => {
        if (phase !== "fading") return
        const fadeCompleteTimer = setTimeout(() => setPhase("exiting"), ROW_FADE_DURATION_MS)
        return () => clearTimeout(fadeCompleteTimer)
    }, [phase])
    useEffect(() => {
        if (phase !== "exiting") return
        const exitTimer = setTimeout(() => onExpire(event), ROW_OPEN_CLOSE_DURATION_MS)
        return () => clearTimeout(exitTimer)
    }, [phase, event, onExpire])

    return (
        <ExpandableContent isOpen={phase !== "entering" && phase !== "exiting"}>
            <div
                className={classNames(
                    "flex items-center gap-2 text-xs text-white-600 rounded-md px-2 py-1.5",
                    {
                        "cursor-pointer hover:bg-white-200": isOnMap,
                        "opacity-0": phase === "entering" || phase === "exiting",
                        "opacity-20": phase === "fading",
                    }
                )}
                style={{
                    transition: `opacity ${
                        phase === "fading" ? ROW_FADE_DURATION_MS : ROW_OPEN_CLOSE_DURATION_MS
                    }ms`,
                }}
                onMouseEnter={() => {
                    if (!isOnMap || phase === "exiting") return
                    setIsHovered(true)
                    if (phase === "fading") setPhase("visible")
                }}
                onMouseLeave={() => {
                    setIsHovered(false)
                }}
                onClick={() => {
                    if (!isOnMap) return
                    const { aircraft } = event
                    setNewViewstate({ latitude: aircraft.lat, longitude: aircraft.lng, zoom: 13 })
                    dispatch(
                        setActivePopup({
                            id: aircraft.id,
                            type: "aircraft",
                            placementType: "live-point",
                            getPoint: () =>
                                getAircraftLivePosition(aircraft.id) ?? {
                                    lat: aircraft.lat,
                                    lng: aircraft.lng,
                                },
                            pointRefreshRateMs: ADSB_DEAD_RECKONING_INTERVAL,
                            renderedDimensions: AIRCRAFT_POPUP_DIMENSIONS,
                            margin: AIRCRAFT_POPUP_MARGIN,
                        })
                    )
                }}
            >
                <Icons.ExtraSmallIcon
                    Icon={type === "Inbound" ? Icons.Inbound : Icons.Outbound}
                    className={classNames({
                        "text-yellow-500": type === "Inbound",
                        "text-white-400": type === "Outbound",
                    })}
                />
                <div className="grow">{type}</div>
                <div>{event.aircraft.callsign}</div>
            </div>
        </ExpandableContent>
    )
}
